import {
  Chat,
  CheckCircle,
  Envelope,
  LinkSimple,
  MegaphoneSimple,
  XCircle,
} from "@phosphor-icons/react";
import clsx from "clsx";
import { useTranslation } from "react-i18next";

import Switcher from "common/components/ui/Switcher";
import useTwBreakpoint from "common/hooks/useTwBreakpoint";
import type { CampaignOutreachType, CampaignType } from "common/types";

import ItemContainer from "./ItemContainer";

const baseMethodOptions = [
  {
    value: "connect",
    label: "Connect",
    icon: <LinkSimple />,
  },
  {
    value: "inmail",
    label: "InMail",
    icon: <Envelope />,
  },
  {
    value: "message",
    label: "Message",
    icon: <Chat />,
  },
] as const;

interface OutreachTypeProps {
  campaignType: CampaignType;
  outreachType: CampaignOutreachType;
  setOutreachType: (newType: CampaignOutreachType) => void;
}

export default function OutreachType({
  campaignType,
  outreachType,
  setOutreachType,
}: OutreachTypeProps) {
  const isDesktop = useTwBreakpoint("lg");

  const isDrip = outreachType === "drip";
  const isGroupOrEvent = campaignType === "group" || campaignType === "event";

  const outreachTypeOptions = baseMethodOptions.filter((option) =>
    isGroupOrEvent ? true : option.value !== "message",
  );

  const isCompactSwitcher = isDesktop && isGroupOrEvent;

  const { t } = useTranslation("campaigns", {
    keyPrefix: "outreachType",
  });

  return (
    <ItemContainer
      icon={MegaphoneSimple}
      title={t("title")}
      description={t("description")}
      infoText={t(isDrip ? "dripInfoText" : "infoText")}
      isDrip={isDrip}
    >
      {isDrip ? (
        <span className="pointer-events-none inline-flex h-10 w-fit items-center gap-x-1 rounded-full bg-black-100 px-4 text-button-xs max-lg:ml-[50px]">
          <Chat size={16} className="-ml-1" /> Message
        </span>
      ) : (
        <div className="flex flex-col gap-2 max-lg:ml-[50px] lg:items-center">
          <Switcher
            size={isCompactSwitcher ? "xs" : "sm"}
            options={outreachTypeOptions}
            selectedOption={outreachType}
            onOptionSelect={(option) =>
              setOutreachType(option as CampaignOutreachType)
            }
          />

          <div className="flex items-center gap-2">
            {outreachTypeOptions.map(({ label, value }) => {
              const isMethodEnabled =
                (outreachType === "inmail" && value === "inmail") ||
                (outreachType === "connect" && value !== "message") ||
                (outreachType === "message" && value !== "connect");

              return (
                <div key={value} className="flex items-center gap-x-0.5">
                  {isMethodEnabled ? (
                    <CheckCircle
                      className="fill-green-500"
                      size={16}
                      weight="fill"
                    />
                  ) : (
                    <XCircle
                      className="fill-black-400"
                      size={16}
                      weight="fill"
                    />
                  )}

                  <span
                    className={clsx(
                      "text-xxs transition-colors",
                      !isMethodEnabled && "text-black-400",
                    )}
                  >
                    {label}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </ItemContainer>
  );
}
